import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { loaderState } from '../Recoil/Atoms';
import { updatePurchaseStatus } from '../api/requests';
import { verifyPaymentStatus, startAggressivePolling } from '../api/orderStatusSocket';

const PaymentRedirector = () => {
  const navigate = useNavigate();
  const [pageLoader, setPageLoader] = useRecoilState(loaderState);

  // Debug logging function
  const debugLog = (message, data = {}) => {
    const timestamp = new Date().toISOString();
    const orderId = localStorage.getItem("orderId") || "unknown";
    console.log(`[PaymentRedirector][Order: ${orderId}] ${message}`, {
      ...data,
      timestamp
    });
  };

  useEffect(() => {
    // Set up event listener for redirection
    window.addEventListener('payment_redirect', handleRedirectionEvent);
    
    debugLog("PaymentRedirector component mounted and listening for redirection events");
    
    // Clean up on unmount
    return () => {
      window.removeEventListener('payment_redirect', handleRedirectionEvent);
      debugLog("PaymentRedirector component unmounted");
    };
  }, []);

  const handleRedirectionEvent = async (event) => {
    const { redirectUrl, orderId, amount, paymentMethod } = event.detail;
    debugLog(`Received redirection event to ${redirectUrl} for order ${orderId}`);
    
    // Show loader during redirection
    setPageLoader(state => ({...state, showLoader: true}));
    
    // Start aggressive polling immediately
    startAggressivePolling();
    
    // Verify payment status
    const paymentVerification = await verifyPaymentStatus();
    if (paymentVerification?.data?.orderStatus === 'PAID') {
      debugLog(`Payment verified as PAID for order ${orderId}`);
    } else {
      debugLog(`Payment status check returned: ${paymentVerification?.data?.orderStatus}`);
    }
    
    // Set success flags
    window.successfulPaymentReceived = true;
    sessionStorage.setItem('successfulPaymentReceived', 'true');
    
    // Set navigation guard
    window.isNavigatingToSuccessPage = true;
    sessionStorage.setItem('isNavigatingToSuccessPage', 'true');
    
    // Update the purchase status in the AddToCart document
    try {
      const sessionId = localStorage.getItem("sessionId") || "";
      debugLog(`Updating purchase status for order ${orderId} with sessionId ${sessionId}`);
      
      // Use the amount from the event detail or a default value
      const finalAmount = amount || localStorage.getItem("finalAmount") || 0;
      // Use the payment method from the event detail or default to "card"
      const finalPaymentMethod = paymentMethod || "card";
      
      updatePurchaseStatus(
        sessionId,
        orderId,
        finalAmount,
        finalPaymentMethod
      ).then(() => {
        debugLog(`Successfully updated purchase status for order ${orderId}`);
      }).catch(error => {
        console.error(`Error updating purchase status: ${error.message}`);
      });
    } catch (error) {
      console.error(`Error in updatePurchaseStatus: ${error.message}`);
    }
    
    // Navigate to the success page
    setTimeout(() => {
      debugLog(`Redirecting to ${redirectUrl}`);
      navigate(redirectUrl);
      
      // Clear localStorage after navigation has started
      setTimeout(() => {
        localStorage.clear();
        setPageLoader(state => ({...state, showLoader: false}));
      }, 100);
    }, 500);
  };

  // This component doesn't render anything visible
  return null;
};

export default PaymentRedirector;
